import {CSSProperties, useEffect, useState} from 'react'
import isEqual from 'lodash/isEqual'
import {useEnvironment} from '@wix/yoshi-flow-editor'
import {useStyles} from '@wix/tpa-settings/react'
import {StyleParamType} from '@wix/tpa-settings'
import type {IStyleParam} from '@wix/tpa-settings'

type ColorParam = IStyleParam<StyleParamType.Color>
type NumberParam = IStyleParam<StyleParamType.Number>

export interface GlobalButtonStyleParams {
  textColor?: ColorParam
  fillColor?: ColorParam
  borderColor?: ColorParam
  borderWidth?: NumberParam
  hoverTextColor?: ColorParam
  hoverFillColor?: ColorParam
  hoverBorderColor?: ColorParam
}

type ColorParamName = Exclude<keyof GlobalButtonStyleParams, 'borderWidth'>

// Where the global primary button colours live: the Studio `wst` vars (hex, always in sync with
// Site Styles > Buttons) and the legacy palette slots (r,g,b, not kept in sync on some sites). There
// are no `wst` hover vars, so hover follows the regular state when the palette is not used.
const globalButtonColorVars: Record<ColorParamName, {wst: string; palette: string}> = {
  fillColor: {wst: '--wst-button-primary-background-color', palette: '--color_48'},
  borderColor: {wst: '--wst-button-primary-border-left-color', palette: '--color_49'},
  textColor: {wst: '--wst-button-primary-color', palette: '--color_50'},
  hoverFillColor: {wst: '--wst-button-primary-background-color', palette: '--color_51'},
  hoverBorderColor: {wst: '--wst-button-primary-border-left-color', palette: '--color_52'},
  hoverTextColor: {wst: '--wst-button-primary-color', palette: '--color_53'},
}

// Same "r,g,b[,a]" format the style engine uses for the param var
const toRgbParts = (color: string) =>
  color
    .match(/[\d.]+/g)
    ?.slice(0, 4)
    .join(',')

const toRgb = (color: string) => toRgbParts(color)?.split(',').slice(0, 3).map(Number).join(',')

const RELATIVE_COLOR_PROBE = 'rgb(from red r g b / alpha)'

// The param vars are used as `rgb(var(--param))`. The hex `wst` vars only fit there through relative
// colour syntax, so browsers without it get the palette-only value instead. SSR and hydration
// assume support; the check runs after mount.
const useSupportsRelativeColor = () => {
  const [supported, setSupported] = useState(true)

  useEffect(() => {
    setSupported(typeof CSS !== 'undefined' && CSS.supports('color', RELATIVE_COLOR_PROBE))
  }, [])

  return supported
}

interface ColorValue {
  value: string
  opacity?: number
}

// Older widget installs saved every style param with its default colour, but under a legacy
// palette name (e.g. `color_8` instead of the slot the default resolves to). Such a colour is
// still the default, so colours are compared by value and opacity, not by name (RST-18559).
const isSameColor = (saved?: ColorValue | null, defaultValue?: ColorValue | null) => {
  const rgb = saved?.value && toRgb(saved.value)

  return (
    !!rgb &&
    !!defaultValue?.value &&
    rgb === toRgb(defaultValue.value) &&
    (saved?.opacity ?? 1) === (defaultValue.opacity ?? 1)
  )
}

const isUnchangedParam = (styles: ReturnType<typeof useStyles>) => (param: IStyleParam) =>
  param.type === StyleParamType.Color
    ? isSameColor(styles.get(param), styles.getDefaultValue(param))
    : isEqual(styles.get(param), styles.getDefaultValue(param))

const toGlobalColorValue = (
  {wst, palette}: {wst: string; palette: string},
  defaultColor: string,
  supportsRelativeColor: boolean,
) => {
  const parts = toRgbParts(defaultColor)
  const paletteValue = parts ? `var(${palette}, ${parts})` : `var(${palette})`

  return supportsRelativeColor
    ? `from var(${wst}, rgb(${paletteValue})) r g b / alpha`
    : paletteValue
}

/**
 * Inline CSS vars that make a button follow the site's global button styles on responsive
 * sites, which includes both Wix Studio and Harmony (RST-18559). Only the params the owner has not
 * changed are redirected, so colours and widths set in the widget settings still win. Classic
 * editor sites are unchanged.
 *
 * Our stylesheets always feed the TPA button vars from the widget style params, and their defaults
 * are classic palette slots, so the global styles never reached the button.
 */
export const useGlobalButtonStyle = (params: GlobalButtonStyleParams): CSSProperties => {
  const {isEditorX} = useEnvironment()
  const styles = useStyles()
  const supportsRelativeColor = useSupportsRelativeColor()

  if (!isEditorX) {
    return {}
  }

  const isUnchanged = isUnchangedParam(styles)
  const style: Record<string, string> = {}

  for (const [name, vars] of Object.entries(globalButtonColorVars)) {
    const param = params[name as ColorParamName]

    if (param && isUnchanged(param)) {
      style[`--${param.key}`] = toGlobalColorValue(
        vars,
        styles.get(param).value,
        supportsRelativeColor,
      )
    }
  }

  if (params.borderWidth && isUnchanged(params.borderWidth)) {
    style[
      '--wix-ui-tpa-button-main-border-width'
    ] = `var(--wst-button-primary-border-left-width, ${styles.get(params.borderWidth)}px)`
  }

  return style as CSSProperties
}

export interface GlobalLineStyleParams {
  color: ColorParam
  width: NumberParam
}

/**
 * Inline CSS vars for the widget dividers: `--globalLineColor` and `--globalLineWidth` point to the
 * site's primary line style (Site Styles > Lines) on responsive sites while the owner has not
 * changed the divider colour or width (RST-18560). The divider rules fall back to the widget's own
 * values when the vars are not set. Lines have no palette slot, so the hex `wst` vars are used.
 */
export const useGlobalLineStyle = ({color, width}: GlobalLineStyleParams): CSSProperties => {
  const {isEditorX} = useEnvironment()
  const styles = useStyles()

  if (!isEditorX) {
    return {}
  }

  const isUnchanged = isUnchangedParam(styles)

  return {
    ...(isUnchanged(color) ? {'--globalLineColor': 'var(--wst-system-line-1-color)'} : {}),
    ...(isUnchanged(width) ? {'--globalLineWidth': 'var(--wst-system-line-1-width)'} : {}),
  } as CSSProperties
}
